<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <router-link
            :to="{ name: 'settings_integrations_ai_agent' }"
            class="p-6 rounded-xl outline outline-1 outline-n-container bg-n-card hover:outline-n-brand transition"
          >
            <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand mb-4"><i class="i-lucide-bot text-2xl" /></div>
            <h3 class="text-heading-1 text-n-slate-12 mb-1">Agente de IA</h3>
            <p class="text-n-slate-11 text-body-main">Configure comportamento, políticas, base de conhecimento e simulações antes de automatizar atendimentos.</p>
          </router-link>
          <router-link
            :to="{ name: 'settings_integrations_meta_conversions' }"
            class="p-6 rounded-xl outline outline-1 outline-n-container bg-n-card hover:outline-n-brand transition"
          >
            <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand mb-4"><i class="i-lucide-chart-no-axes-combined text-2xl" /></div>
            <h3 class="text-heading-1 text-n-slate-12 mb-1">Meta & Conversões</h3>
            <p class="text-n-slate-11 text-body-main">Conecte Pixel/Dataset, registre eventos CAPI e acompanhe os envios dos agentes.</p>
          </router-link>
          <IntegrationItem
            v-for="item in filteredIntegrationList"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
